import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardContent,
  Checkbox,
  InputField,
  SubmitButton,
} from '@saas-kit/ui';

export interface CustomerTypeCreateInput {
  code: string;
  name: string;
  rank: number;
  is_default: boolean;
}

/**
 * Create form for a QMS customer type. Split out from the tab so the tab
 * stays focused on the list. A lower rank is served first, so the rank hint
 * tells the operator a priority tier needs a smaller number than the default.
 */
export function CustomerTypeCreateForm({
  creating,
  onCreate,
}: {
  creating: boolean;
  onCreate: (input: CustomerTypeCreateInput) => Promise<unknown> | void;
}): JSX.Element {
  const { t } = useTranslation('qms');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [rank, setRank] = useState('100');
  const [isDefault, setIsDefault] = useState(false);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!code.trim() || !name.trim()) return;
    await onCreate({
      code: code.trim(),
      name: name.trim(),
      rank: Number(rank) || 100,
      is_default: isDefault,
    });
    setCode('');
    setName('');
    setRank('100');
    setIsDefault(false);
  };

  return (
    <Card>
      <CardContent className="p-5">
        <form onSubmit={(e) => void submit(e)} className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <InputField
              label={t('fields.code')}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={t('placeholders.customer_type_code')}
              required
            />
            <InputField
              label={t('fields.name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('placeholders.customer_type_name')}
              required
            />
            <InputField
              label={t('fields.rank')}
              type="number"
              min={0}
              value={rank}
              onChange={(e) => setRank(e.target.value)}
              placeholder={t('placeholders.customer_type_rank')}
              hint={t('hints.rank')}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-foreground">
            <Checkbox
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
            />
            {t('fields.default')}
          </label>
          <SubmitButton
            loading={creating}
            label={t('actions.create')}
            loadingLabel={t('actions.create')}
          />
        </form>
      </CardContent>
    </Card>
  );
}
